import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Modal, FlatList, ActivityIndicator, Pressable } from 'react-native';
import { Colors } from '@/constants/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FontAwesome, Ionicons } from '@expo/vector-icons';
import { getStreamClient } from '@/services/stream/client';
import { useNotification } from '@/services/notifications/provider';
import { useNotificationsManager } from '@/hooks/notifications/useNotificationsManager';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { ProfileSection } from '@/components/settings/ProfileSection';
import { NotificationToggle } from '@/components/settings/NotificationToggle';
import { LogoutButton } from '@/components/settings/LogoutButton';

export function TabletHeader() {
    const insets = useSafeAreaInsets();
    const client = getStreamClient();
    const user = client.user;
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const { unreadCount } = useNotification();

    const {
        notifications,
        isLoading,
        handleNotificationPress,
        handleDelete,
        loadNotifications
    } = useNotificationsManager();

    // Initial load seeds unreadCount (same counter the native tab badge uses);
    // afterwards the new_lead Stream event keeps it fresh in real time.
    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    useEffect(() => {
        if (showNotifications) {
            loadNotifications();
        }
    }, [showNotifications, loadNotifications]);


    return (
        <View style={[styles.container, { paddingTop: insets.top }]}>
            <View style={styles.headerContent}>
                <View style={styles.leftSection}>
                    <View style={styles.logoContainer}>
                        <Image source={require('@/assets/images/WhiteNoBgLogo.png')} style={{width: 28, height: 28}}/>
                    </View>
                    <Text style={styles.headerTitle}>Nōmadas Chat</Text>
                </View>
                
                <View style={styles.rightSection}>
                    <TouchableOpacity 
                        style={styles.iconButton} 
                        activeOpacity={0.7}
                        onPress={() => setShowNotifications(true)}
                    >
                        <FontAwesome name="bell" size={20} color="white" />
                        {unreadCount > 0 && (
                            <View style={styles.notificationBadge}>
                                <Text style={styles.notificationBadgeText}>
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </Text>
                            </View>
                        )}
                    </TouchableOpacity>
                    
                    <TouchableOpacity 
                        style={styles.profileButton} 
                        activeOpacity={0.7}
                        onPress={() => setShowProfile(true)}
                    >
                        <Image 
                            source={{ uri: user?.image || 'https://getstream.io/random_png/?name=' + (user?.name || 'User') }} 
                            style={styles.avatar} 
                        />
                    </TouchableOpacity>
                </View>
            </View>

            <Modal
                visible={showNotifications}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowNotifications(false)}
            >
                <Pressable 
                    style={styles.modalOverlay} 
                    onPress={() => setShowNotifications(false)}
                >
                    <View style={[styles.popoverContainer, { top: insets.top + 50 }]}>
                        <View style={styles.popoverHeader}>
                            <Text style={styles.popoverTitle}>Notifications</Text>
                            <TouchableOpacity onPress={() => setShowNotifications(false)}>
                                <Ionicons name="close" size={24} color="#6B7280" />
                            </TouchableOpacity>
                        </View>

                        {isLoading && notifications.length === 0 ? (
                            <View style={styles.centerContent}>
                                <ActivityIndicator size="small" color={Colors.brand} />
                            </View>
                        ) : (
                            <FlatList
                                data={notifications}
                                renderItem={({ item }) => (
                                    <NotificationItem 
                                        item={item} 
                                        onPress={(item) => handleNotificationPress(item, () => setShowNotifications(false))} 
                                        onDelete={handleDelete} 
                                    />
                                )}
                                keyExtractor={(item) => item.id}
                                ListEmptyComponent={
                                    <View style={styles.centerContent}>
                                        <Text style={styles.emptyText}>No notifications yet</Text>
                                    </View>
                                }
                                ItemSeparatorComponent={() => <View style={styles.separator} />}
                                contentContainerStyle={styles.listContent}
                            />
                        )}
                    </View>
                </Pressable>
            </Modal>

            <Modal
                visible={showProfile}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowProfile(false)}
            >
                <Pressable 
                    style={styles.modalOverlay} 
                    onPress={() => setShowProfile(false)}
                >
                    <View style={[styles.profilePopover, { top: insets.top + 50 }]}>
                        <View style={styles.profileHeader}>
                            <ProfileSection />
                            <TouchableOpacity onPress={() => setShowProfile(false)} style={styles.closeButton}>
                                <Ionicons name="close" size={24} color="#6B7280" />
                            </TouchableOpacity>
                        </View>

                        <View style={styles.profileContent}>
                            <NotificationToggle />

                            <LogoutButton />
                        </View>
                    </View>
                </Pressable>
            </Modal>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: Colors.brand,
        zIndex: 10,
    },
    headerContent: {
        height: 45,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
    },
    leftSection: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    logoContainer: {
        marginRight: 10,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: 'white',
        letterSpacing: 0.5,
    },
    rightSection: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    iconButton: {
        padding: 8,
        marginRight: 15,
        position: 'relative',
    },
    notificationBadge: {
        position: 'absolute',
        top: 2,
        right: 0,
        minWidth: 16,
        height: 16,
        borderRadius: 8,
        paddingHorizontal: 4,
        backgroundColor: '#EF4444',
        borderWidth: 1,
        borderColor: Colors.brand,
        alignItems: 'center',
        justifyContent: 'center',
    },
    notificationBadgeText: {
        color: 'white',
        fontSize: 10,
        fontWeight: '700',
        lineHeight: 12,
    },
    profileButton: {
        borderRadius: 20,
        overflow: 'hidden',
    },
    avatar: {
        width: 28,
        height: 28,
        borderRadius: 18,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.1)',
    },
    popoverContainer: {
        position: 'absolute',
        right: 20,
        width: 450,
        maxHeight: 500,
        backgroundColor: 'white',
        borderRadius: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 10,
        elevation: 5,
        overflow: 'hidden',
    },
    popoverHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    popoverTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111827',
    },
    listContent: {
        flexGrow: 1,
    },
    separator: {
        height: 1,
        backgroundColor: '#F3F4F6',
    },
    centerContent: {
        padding: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyText: {
        color: '#9CA3AF',
        fontSize: 14,
    },
    profilePopover: {
        position: 'absolute',
        right: 20,
        width: 380,
        backgroundColor: 'white',
        borderRadius: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 10,
        elevation: 5,
        padding: 20,
    },
    profileHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 20,
    },
    closeButton: {
        padding: 4,
    },
    profileContent: {
        gap: 16,
    },
});
